import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { SearchResults, SearchResultType } from '@klausapp/app-navigation/types'
import i18n from '@/i18n'
import useRoutes from '@/modules/application/components/CommandPalette/utils/useRoutes'
import usePaletteFilters from '@/modules/application/components/CommandPalette/utils/usePaletteFilters'
import useConversationSearch from '@/modules/application/components/CommandPalette/utils/useConversationSearch'

export function useNavigationSearch() {
  const router = useRouter()
  const searchQuery = ref<string>('')
  const searchResults = ref<SearchResults | null>(null)

  const routes = useRoutes(searchQuery)
  const { filters, selectFilter } = usePaletteFilters(searchQuery)
  const { searching, conversations, openSearchResults } = useConversationSearch(searchQuery)

  watch(
    [routes, filters, conversations, searching],
    () => {
      searchResults.value = {
        query: searchQuery.value,
        routes: routes.value.map((r) => ({
          uuid: r.uuid,
          name: r.name,
          formattedName: r.formattedName,
          type: 'route' as const,
          icon: r.icon,
          typeKey: r.typeKey,
          label: r.type,
          displayName: r.formattedName,
        })),
        filters: filters.value.map((f) => ({
          uuid: f.uuid,
          name: f.name,
          isPublic: f.isPublic,
          filterId: f.filterId,
          typeKey: 'filter' as const,
          label: f.isPublic
            ? i18n.t('components.command_palette.type_public_filter')
            : i18n.t('components.command_palette.type_private_filter'),
          type: 'filter' as const,
          displayName: f.name,
        })),
        conversations: conversations.value.map((c) => ({
          uuid: c.uuid,
          externalId: c.externalId,
          label: i18n.t('components.command_palette.type_conversation'),
          type: 'conversation' as const,
          typeKey: 'conversations' as const,
          displayName: i18n.t('components.command_palette.type_conversation'),
        })),
        searching: searching.value,
      }
    },
    { immediate: true },
  )

  const onSearchQueryChange = (query: string) => {
    searchQuery.value = query
  }

  const onSearchResultSelect = (type: SearchResultType, id: string, _data?: unknown) => {
    if (type === 'filter') {
      const filter = filters.value.find((f) => f.uuid === id)
      if (filter) {
        selectFilter(filter.filterId)
      }
    } else if (type === 'conversation') {
      openSearchResults()
    } else if (type === 'route') {
      const route = routes.value.find((r) => r.uuid === id)
      if (route && route.name) {
        router.push({ name: route.name })
      }
    }
  }

  return {
    searchQuery,
    searchResults,
    onSearchQueryChange,
    onSearchResultSelect,
  }
}
